import React, { useState, useEffect } from 'react';
import {
  Button,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { Heart, Check } from 'lucide-react';
import EventService from '../../services/eventService';

const ParticipationButton = ({ 
  eventId, 
  getAuthHeaders, 
  onParticipationChange = null,
  size = 'medium'
}) => {
  const [participationStatus, setParticipationStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load initial participation status
  useEffect(() => {
    const loadParticipationStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Get user's participation status
        const statusResponse = await EventService.getUserEventStatus(eventId, getAuthHeaders);
        setParticipationStatus(statusResponse.participationStatus);
      } catch (error) {
        console.error('Error loading participation status:', error);
        setError('Failed to load participation status');
        setParticipationStatus(null);
      } finally {
        setIsLoading(false);
      }
    };

    if (eventId && getAuthHeaders) {
      loadParticipationStatus();
    }
  }, [eventId, getAuthHeaders]);

  const handleParticipationChange = async (targetStatus) => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      setError(null);

      // If clicking the same button that's already active, cancel it (set to not-going)
      const newStatus = participationStatus === targetStatus ? 'not-going' : targetStatus;
      
      const result = await EventService.updateParticipationStatus(
        eventId, 
        newStatus, 
        getAuthHeaders
      );
      
      // Update local state based on the action
      const finalStatus = newStatus === 'not-going' ? null : newStatus;
      setParticipationStatus(finalStatus);

      // Call callback if provided
      if (onParticipationChange) {
        onParticipationChange(finalStatus, result);
      }

    } catch (error) {
      console.error('Error updating participation:', error);
      setError('Failed to update participation');
    } finally {
      setIsLoading(false);
    }
  };

  const getButtonVariant = (status) => {
    if (participationStatus === status) {
      return 'contained';
    }
    return 'outlined';
  };

  const getButtonColor = (status) => {
    if (error) return 'error';
    if (participationStatus === status) {
      return status === 'interested' ? 'secondary' : 'primary';
    }
    return 'primary';
  };

  const buttonSize = size === 'small' ? 'small' : 'medium';
  const iconSize = size === 'small' ? 16 : 20;

  if (error) {
    return (
      <Box>
        <Typography variant="body2" color="error">
          {error}
        </Typography>
        <Button 
          size={buttonSize}
          onClick={() => window.location.reload()}
          variant="outlined"
          sx={{ mt: 1 }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box display="flex" gap={1}>
      {/* Interested Button */}
      <Button
        variant={getButtonVariant('interested')}
        color={getButtonColor('interested')}
        onClick={() => handleParticipationChange('interested')}
        disabled={isLoading}
        size={buttonSize}
        startIcon={
          isLoading && participationStatus === 'interested' ? (
            <CircularProgress size={iconSize} />
          ) : (
            <Heart 
              size={iconSize} 
              fill={participationStatus === 'interested' ? 'currentColor' : 'none'}
              stroke="currentColor"
            />
          )
        }
        sx={{
          backgroundColor: participationStatus === 'interested' ? undefined : '#f8fafc',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: participationStatus === 'interested' ? undefined : '#e8f5fd',
            color: '#3a7bd5',
          },
        }}
      >
        Interested
      </Button>
      
      {/* Going Button */}
      <Button
        variant={getButtonVariant('going')}
        color={getButtonColor('going')}
        onClick={() => handleParticipationChange('going')}
        disabled={isLoading}
        size={buttonSize}
        startIcon={
          isLoading && participationStatus === 'going' ? (
            <CircularProgress size={iconSize} />
          ) : (
            <Check size={iconSize} />
          )
        }
        sx={{
          backgroundColor: participationStatus === 'going' ? undefined : '#f8fafc',
          border: '1px solid #e0e0e0',
          borderRadius: '8px',
          '&:hover': {
            backgroundColor: participationStatus === 'going' ? undefined : '#e8f5fd',
            color: '#3a7bd5',
          },
        }}
      >
        Going
      </Button>
    </Box>
  );
};

export default ParticipationButton;